// Placar real ao lado do público: o admin confere o que está escondido pelo
// congelamento sem precisar revelar.

import { useQuery } from "@tanstack/react-query";
import { ChartNoAxesColumn, Eye, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import type { ScoreboardDto } from "@compila/shared";
import { api } from "../../lib/api.js";
import { Button } from "../../components/motion/button/base";
import { AlertMessage } from "../../components/alerts";
import { Scoreboard } from "../../components/Scoreboard.js";
import { FrozenBanner, Loading, PageHeader } from "../../components/domain.js";

const errorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Não foi possível carregar o placar.";

export function AdminScoreboardPage(): React.JSX.Element {
  const real = useQuery({
    queryKey: ["admin-scoreboard"],
    queryFn: () => api.get<ScoreboardDto>("/admin/scoreboard"),
  });

  const publico = useQuery({
    queryKey: ["public-scoreboard"],
    queryFn: () => api.get<ScoreboardDto>("/public/scoreboard"),
    refetchInterval: 15_000,
  });

  if (real.isPending) return <Loading label="Carregando o placar…" />;
  if (real.error) {
    return (
      <div className="space-y-4">
        <AlertMessage tone="error">{errorMessage(real.error)}</AlertMessage>
        <Button variant="secondary" onClick={() => void real.refetch()}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  const frozen = publico.data?.contest.frozen ?? false;

  return (
    <div className="space-y-7">
      <PageHeader
        title="Placar"
        description="A visão real, sem congelamento. Só administradores a enxergam."
        actions={
          <Link
            to="/telao"
            target="_blank"
            rel="noreferrer"
            className="inline-flex h-10 items-center gap-2 rounded-xl border border-border bg-card px-4 text-sm transition-colors hover:bg-secondary"
          >
            Abrir telão <ArrowUpRight size={16} aria-hidden />
          </Link>
        }
      />

      {frozen && (
        <AlertMessage tone="warning">
          O placar público está congelado. Abaixo está o resultado real — compare com a
          versão pública no fim da página antes de revelar.
        </AlertMessage>
      )}

      <section className="overflow-hidden rounded-2xl border border-border bg-card">
        <header className="flex items-center gap-2 border-b border-border px-5 py-4">
          <Eye size={16} className="text-muted-foreground" aria-hidden />
          <h2 className="text-sm font-medium">Placar real</h2>
        </header>
        <div className="px-2 py-1">
          <Scoreboard board={real.data} />
        </div>
      </section>

      {frozen && publico.data && (
        <section className="overflow-hidden rounded-2xl border border-accent/40 bg-card">
          <header className="flex flex-wrap items-center gap-3 border-b border-border px-5 py-4">
            <ChartNoAxesColumn size={16} className="text-muted-foreground" aria-hidden />
            <h2 className="text-sm font-medium">O que o público vê</h2>
            <FrozenBanner />
          </header>
          <div className="px-2 py-1">
            <Scoreboard board={publico.data} frozen />
          </div>
        </section>
      )}
    </div>
  );
}
